/**
 * #44 Yasaklı Kelime detayı.
 *
 * Kelime SONRADAN DEĞİŞTİRİLEBİLİR — geçmiş ihlal kayıtları yakalanan kelimeyi METİN olarak
 * saklar ve bu tanıma FK ile bağlı değildir; dolayısıyla değiştirmek hiçbir izi bozmaz.
 * (Karşılaştır: VisitActivityType.Code sabittir çünkü geçmiş hareketler ona bağlıdır.)
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  DetailShell, Section, Grid, FieldText, FieldSelect, useIsViewMode,
} from './detail/DetailShell';

const API_BASE = '/api/teamchatbannedwords';
const LIST_ROUTE = '/team-chat-banned-words';

// Serbest metin alanıdır; bunlar yalnız hazır seçeneklerdir (raporlama kırılımı).
const CATEGORY_OPTIONS = [
  { value: '', label: '(Kategori yok)' },
  { value: 'RAKIP', label: 'Rakip adı' },
  { value: 'KUFUR', label: 'Küfür / hakaret' },
  { value: 'GIZLI', label: 'Gizli bilgi' },
  { value: 'DIGER', label: 'Diğer' },
];

const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Aktif' },
  { value: 'false', label: 'Pasif' },
];

const EMPTY = { word: '', category: '', isActive: 'true' };

export function TeamChatBannedWordDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const isView = useIsViewMode() && !isNew;
  const { canAdd, canEdit } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  usePageMeta(isNew ? 'Yeni Yasaklı Kelime' : 'Yasaklı Kelime Detayı',
    'Ekip mesajlaşmasında maskelenen kelimeler.');

  const [form, setForm] = useState(EMPTY);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const set = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/GetById?id=${id}`, { headers: authHeaders() });
        const payload = await res.json();
        const d = payload?.data ?? payload?.Data ?? payload;
        if (cancelled || !d) return;
        setForm({
          word: d.word ?? '',
          category: d.category ?? '',
          isActive: String(d.isActive ?? true),
        });
        setAudit({
          createdBy: d.createdBy, createdDate: d.createdDate,
          updatedBy: d.updatedBy, updatedDate: d.updatedDate,
        });
      } catch {
        if (!cancelled) showToast('error', 'Kayıt yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  async function handleSave() {
    setTried(true);
    const word = form.word?.trim() ?? '';
    if (!word) {
      showToast('error', 'Kelime zorunludur.');
      return;
    }
    // Backend de denetler; burada erken uyarı vermek kullanıcıyı sunucuya gitmeden bilgilendirir.
    if (!/[\p{L}\p{N}]/u.test(word)) {
      showToast('error', 'Kelime en az bir harf veya rakam içermelidir (yalnız noktalama eşleşmez).');
      return;
    }

    setSaving(true);
    try {
      const body = {
        ...(isNew ? {} : { id }),
        word,
        category: form.category || null,
        isActive: form.isActive === 'true',
      };
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok || payload?.success === false || payload?.Success === false) {
        throw new Error(payload?.message || payload?.Message || 'Kaydedilemedi.');
      }
      showToast('success', 'Kaydedildi.');
      // Repo konvansiyonu: başarılı kayıttan sonra listeye dön.
      setTimeout(() => navigate(LIST_ROUTE), 400);
    } catch (e) {
      showToast('error', e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <DetailShell
      readOnly={isView}
      loading={loading}
      crumb={[
        { label: 'Yasaklı Kelimeler', to: LIST_ROUTE },
        { label: isNew ? 'Yeni' : (isView ? 'Görüntüle' : 'Detay'), current: true },
      ]}
      audit={isNew ? null : audit}
      actions={!isView && canSave && (
        <button type="button" className="dps-btn primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      )}
    >
      <div className="dps-form">
        <Section
          icon="list"
          title="Kelime Tanımı"
          subtitle="Mesajda geçerse maskelenir; mesaj engellenmez ve ham metin denetimde saklanır."
        >
          <Grid cols={2}>
            <FieldText
              form={form} set={set} tried={tried} k="word" label="Kelime" req
              hint="Tek kelime ya da ifade olabilir. Eşleşme KELİME BAZLIDIR: 'sik' tanımı 'klasik'i
                    bozmaz. Büyük/küçük harf ve Türkçe aksan farkı önemsizdir — 'rakıp' yazan da
                    'rakip' tanımına takılır."
            />
            <FieldSelect
              form={form} set={set} k="category" label="Kategori" options={CATEGORY_OPTIONS}
              hint="Yalnız raporlama kırılımı; davranışı etkilemez."
            />
            <FieldSelect
              form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS}
              hint="Pasif tanım denetime girmez — kaydı silmeden geçici olarak devre dışı bırakır."
            />
          </Grid>
        </Section>
      </div>

      <Toast toast={toast} />
    </DetailShell>
  );
}
